import {
  CalendarOutlined,
  CreditCardOutlined,
  FormOutlined,
  GiftOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import MembershipProfile from '@components/membership/membership-profile/MembershipProfile'
import { MembershipProfileContext } from '@contexts/membership/MembershipProfileContext'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { resetEventParticipationQueryAndIds } from '@store/membership/EventParticipationReducer'
import {
  getMembershipProfileWithId,
  selectMembershipProfileById,
} from '@store/membership/MembershipProfileReducer'
import { extractSectionFromPath } from '@supplement-bacon/react-toolkit/utils'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { useSBRM } from '@supplement-bacon/sbrm-react'
import {
  Affix,
  Alert,
  Card,
  Col,
  Divider,
  Flex,
  Menu,
  MenuProps,
  Row,
  Spin,
  Tag,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'

const { Text } = Typography

type Props = {
  id: number
}

const MembershipProfileSectionsLayout = ({ id }: Props) => {
  const dispatch = useAppDispatch()
  const { isOpen } = useSBRM()
  const { pathname } = useLocation()
  const membershipProfile = useAppSelector(selectMembershipProfileById(id))

  const [selectedKey, setSelectedKey] = useState<string>('subscription')

  const fetchData = () => {
    dispatch(getMembershipProfileWithId(id))
    dispatch(resetEventParticipationQueryAndIds(initialQuery))
  }

  useEffect(() => {
    fetchData()
  }, [id])

  useEffect(() => {
    if (isOpen) return
    fetchData()
  }, [isOpen])

  useEffect(() => {
    setSelectedKey(extractSectionFromPath(pathname, 4))
  }, [pathname])

  if (!membershipProfile) {
    return <Spin />
  }

  const sections = [
    {
      key: 'subscription',
      label: 'Subscription',
      icon: <CreditCardOutlined />,
      disabled: false,
    },
    {
      key: 'event-participation',
      label: 'Event Participations',
      icon: <CalendarOutlined />,
      disabled: !membershipProfile.hasAppUser,
    },
    {
      key: 'form-answers',
      label: 'Form Answers',
      icon: <FormOutlined />,
      disabled: false,
    },
    {
      key: 'subscription-activities',
      label: "Subscription's Activities",
      icon: <HistoryOutlined />,
      disabled: false,
    },
    {
      key: 'profile-activities',
      label: "Profile's Activities",
      icon: <HistoryOutlined />,
      disabled: false,
    },
    {
      key: 'complementary-items',
      label: 'Balances and credits',
      icon: <GiftOutlined />,
      disabled: false,
    },
  ]

  const menuItems: MenuProps['items'] = sections.map(
    ({ key, label, icon, disabled }) => ({
      key,
      label: <Link to={key}>{label}</Link>,
      icon,
      disabled,
    })
  )

  return (
    <MembershipProfileContext.Provider value={{ id }}>
      <Row gutter={[24, 24]}>
        <Col sm={7} xs={24}>
          <Flex vertical gap={24}>
            <Affix offsetTop={0}>
              <Card
                styles={{ body: { padding: 0, overflow: 'hidden' } }}
                variant="borderless"
              >
                <Flex justify="flex-end" style={{ padding: 15 }}>
                  <MembershipProfile.ActionDropdown id={membershipProfile.id} />
                </Flex>
                <Flex
                  vertical
                  gap={10}
                  style={{
                    paddingLeft: 30,
                    paddingRight: 30,
                    paddingBottom: 30,
                  }}
                >
                  <MembershipProfile.Avatar
                    model={membershipProfile}
                    direction="vertical"
                    size="large"
                    showAttributes
                    noLinkToDetails
                    pictureOpensInModal
                  />
                  {membershipProfile.hasAppUser &&
                    membershipProfile.codeUsedAt && (
                      <Text type="secondary">
                        Activation code used on{' '}
                        {dayjs(membershipProfile.codeUsedAt).format(
                          'DD/MM/YYYY HH:mm'
                        )}
                      </Text>
                    )}
                  {!membershipProfile.hasAppUser && (
                    <Alert
                      type="info"
                      title={
                        <Flex justify="space-between" align="center">
                          <Text type="secondary">Activation code</Text>
                          <Tag>
                            <Text copyable>{membershipProfile.code}</Text>
                          </Tag>
                        </Flex>
                      }
                    />
                  )}
                </Flex>
                <Divider style={{ margin: 0 }} />
                <Flex vertical gap={5} style={{ padding: 20 }} wrap>
                  <Text type="secondary">Tags :</Text>
                  <MembershipProfile.TagManager
                    id={membershipProfile.id}
                    onChange={() => window.location.reload()}
                  />
                </Flex>
                <Divider style={{ margin: 0 }} />
                <Menu
                  selectedKeys={[selectedKey]}
                  style={{ borderWidth: 0, padding: 16 }}
                  mode="vertical"
                  items={menuItems}
                />
              </Card>
            </Affix>
          </Flex>
        </Col>
        <Col sm={17} xs={24}>
          <Flex vertical>
            {membershipProfile.isActive === false && (
              <Alert
                type="error"
                showIcon
                title="Restricted Member"
                description="This member’s access and privileges have been restricted. They no longer have access to their benefits and can no longer view the membership section within the application."
                style={{ marginBottom: 20 }}
              />
            )}
            <Outlet />
          </Flex>
        </Col>
      </Row>
    </MembershipProfileContext.Provider>
  )
}

export default MembershipProfileSectionsLayout
